import type { ReactNode } from 'react';

/**
 * CasePageHeader Component
 * 
 * Layout-level header for all case pages.
 * Renders page title, optional subtitle, and optional action slot (right-aligned).
 * 
 * Responsibilities:
 * - Page title
 * - Optional subtitle
 * - Optional action slot (right-aligned)
 * 
 * ZERO navigation, ZERO routing logic.
 */

interface CasePageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
}

export function CasePageHeader({ title, subtitle, actions, icon: Icon }: CasePageHeaderProps) {
  return (
    <header 
      data-testid="case-page-header" 
      className="bg-slate-900 border-b border-slate-700 px-6 py-4"
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0 flex items-center gap-3">
          {Icon && <Icon className="h-6 w-6 text-slate-400 flex-shrink-0" />}
          <h1 
            className="text-2xl font-semibold text-slate-100 truncate"
            data-owned-by="CasePageHeader"
          >
            {title}
          </h1>
          {subtitle && (
            <p className="mt-1 text-sm text-slate-400">
              {subtitle}
            </p>
          )}
        </div>
        
        {actions && (
          <div className="ml-4 flex items-center gap-3 flex-shrink-0">
            {actions}
          </div>
        )}
      </div>
    </header>
  );
}
